import { PaginatedTanstackQueryParams, RetrieveByUuidParams } from '@/lib/types/v1'
import { ref, toValue, watchEffect } from 'vue'
import { usePaginatedTanstackQueryParams } from '@/composables/helpers'
import { useQuery } from '@tanstack/vue-query'
import { httpClientWithoutInterceptors } from '@/utils/httpClient'
import { isArray, isBoolean, isNumber, parseAmpResponse } from '@/lib/utils'
import { queryKeys } from '@/lib/query-keys/v1'
import {
    paginatedCloudflareServersResponseSchema,
    retrieveCloudflareServerResponseSchema,
} from '@/lib/schema/v2/cloudflare-server'
import { useRoute } from '@/composables/route'

export const usePaginatedCloudflareServersQuery = (
    params?: PaginatedTanstackQueryParams | (() => PaginatedTanstackQueryParams)
) => {
    const route = useRoute()
    const enabled = ref(true)
    const staleTime = ref(0)
    const { page, limit, sort, filter, q, view, groupBy } = usePaginatedTanstackQueryParams(params)
    const keys = ref(queryKeys.cloudflareServers.list(route))

    watchEffect(() => {
        const value: PaginatedTanstackQueryParams | undefined = toValue(params)

        if (isBoolean(value?.enabled)) {
            enabled.value = value.enabled
        }

        if (isNumber(value?.staleTime)) {
            staleTime.value = value.staleTime
        }

        if (isArray(value?.queryKeys)) {
            keys.value = value.queryKeys
        }
    })

    return useQuery({
        queryKey: keys,
        queryFn: async () => {
            const { data } = await httpClientWithoutInterceptors.get('v1/cloudflare/servers', {
                params: {
                    page: page.value,
                    limit: limit.value,
                    sort: sort.value,
                    filter: filter.value,
                    q: q.value,
                    view: view.value,
                    group_by: groupBy.value,
                },
            })

            return parseAmpResponse(data, paginatedCloudflareServersResponseSchema)
        },
        select: (data) => {
            return parseAmpResponse(data, paginatedCloudflareServersResponseSchema)
        },
        enabled: enabled,
        staleTime: staleTime,
    })
}

export const useRetrieveCloudflareServerByUuidQuery = (
    params: RetrieveByUuidParams | (() => RetrieveByUuidParams)
) => {
    const staleTime = ref(0)
    const enabled = ref(true)
    const refetchOnWindowFocus = ref(true)
    const keys = ref(null)
    const uuid = ref(null)

    watchEffect(() => {
        const value: RetrieveByUuidParams | undefined = toValue(params)

        uuid.value = value.uuid
        keys.value = queryKeys.cloudflareServers.item(uuid.value)

        if (isNumber(value?.staleTime)) {
            staleTime.value = value.staleTime
        }

        if (isBoolean(value?.enabled)) {
            enabled.value = value.enabled
        }

        if (isBoolean(value?.refetchOnWindowFocus)) {
            refetchOnWindowFocus.value = value.refetchOnWindowFocus
        }

        if (isArray(value?.queryKeys)) {
            keys.value = value.queryKeys
        }
    })

    return useQuery({
        queryKey: keys,
        queryFn: async () => {
            const { data } = await httpClientWithoutInterceptors.get(
                `v1/cloudflare/servers/${uuid.value}`
            )

            return parseAmpResponse(data, retrieveCloudflareServerResponseSchema)
        },
        select: (data) => {
            return parseAmpResponse(data, retrieveCloudflareServerResponseSchema)
        },
        enabled: enabled,
        staleTime: staleTime,
        refetchOnWindowFocus: refetchOnWindowFocus,
    })
}
